import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useLocale } from '../../contexts/LocaleContext';

type ToastType = 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) clearTimeout(timer);
      timers.current.clear();
    },
    []
  );

  const toast = useCallback(
    (type: ToastType, message: string) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, type, message }]);
      timers.current.set(
        id,
        setTimeout(() => remove(id), 4000)
      );
    },
    [remove]
  );

  const success = useCallback((msg: string) => toast('success', msg), [toast]);
  const error = useCallback((msg: string) => toast('error', msg), [toast]);
  const warning = useCallback((msg: string) => toast('warning', msg), [toast]);
  const contextValue = useMemo(() => ({ toast, success, error, warning }), [toast, success, error, warning]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 flex flex-col items-end gap-2 pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onRemove={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast: t, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const { t: translate } = useLocale();
  const icons: Record<ToastType, React.ReactNode> = {
    success: <CheckCircle size={16} className="text-emerald-400 shrink-0" aria-hidden="true" />,
    error: <XCircle size={16} className="text-red-400 shrink-0" aria-hidden="true" />,
    warning: <AlertCircle size={16} className="text-yellow-400 shrink-0" aria-hidden="true" />,
  };
  const bg: Record<ToastType, string> = {
    success: 'border-emerald-500/50',
    error: 'border-red-500/50',
    warning: 'border-yellow-500/50',
  };

  return (
    <div
      role={t.type === 'error' ? 'alert' : 'status'}
      aria-live={t.type === 'error' ? 'assertive' : 'polite'}
      className={clsx(
        'pointer-events-auto flex w-full items-center gap-3 rounded-lg border bg-slate-900 px-4 py-3 shadow-xl sm:w-auto sm:min-w-64 max-w-sm',
        bg[t.type]
      )}
    >
      {icons[t.type]}
      <span className="min-w-0 wrap-break-word wrap-anywhere text-sm text-slate-200 flex-1">{t.message}</span>
      <button
        type="button"
        onClick={() => onRemove(t.id)}
        aria-label={translate('dismissNotification')}
        title={translate('dismissNotification')}
        className="shrink-0 rounded-md p-2 text-slate-400 hover:text-slate-100 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
